import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchCronJobById,
  updateCronJobs,
  clearCronJob,
  fetchCronLogs, // Updated thunk: accepts a cron job id.
} from '../redux/slices/cronSlice';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';

// Modal component for popup display
const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 flex items-center justify-center z-50">
    {/* Overlay */}
    <div
      className="fixed inset-0 bg-black opacity-50"
      onClick={onClose}
    ></div>
    {/* Popup content */}
    <div className="bg-white rounded-lg p-6 relative z-10 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
      <button
        className="absolute top-2 right-2 text-gray-700 text-2xl font-bold"
        onClick={onClose}
      >
        &times;
      </button>
      {children}
    </div>
  </div>
);

// CronLogsComponent displays the logs in a table
const CronLogsComponent = ({ logs, loading, error }) => {
  // Format created_at using UTC so that "2025-02-23T21:00:04.000Z" becomes "2025-02-23 21:00:04"
  const formatCreatedAt = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Format execution time (duration in ms) to display only "HH:mm:ss"
  const formatExecutionTime = (ms) => {
    if (ms === undefined || ms === null) return '-';
    // Using UTC time so that, for example, 4000 ms becomes "00:00:04"
    return new Date(Number(ms)).toISOString().substr(11, 8);
  };

  if (loading) return <p>Loading logs...</p>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (!logs || logs.length === 0)
    return <p>No logs available for this cron job.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300 text-sm">
        <thead>
          <tr className="bg-gray-200">
            <th className="border p-2">ID</th>
            <th className="border p-2">Name</th>
            <th className="border p-2">Status</th>
            <th className="border p-2">Created At</th>
            <th className="border p-2">Execution Time</th>
            <th className="border p-2">Response</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="hover:bg-gray-100">
              <td className="border p-2 text-center">{log.cron_job_id}</td>
              <td className="border p-2">{log.name}</td>
              <td className="border p-2 text-center">{log.status}</td>
              <td className="border p-2 text-center">
                {formatCreatedAt(log.created_at)}
              </td>
              <td className="border p-2 text-center">
                {formatExecutionTime(log.execution_time_diff)}
              </td>
              <td className="border p-2 text-center">
                {(log.stdout)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


const UpdateCronJob = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Retrieve cron job details and logs from the Redux store
  const cronJob = useSelector((state) => state.cron.cronJob);
  const cronLogs = useSelector((state) => state.cron.cronLogs);
  const loading = useSelector((state) => state.cron.loading);
  const error = useSelector((state) => state.cron.error);

  const [formData, setFormData] = useState({
    name: '',
    command: '',
    description: '',
    status: 'active',
    scheduleType: 'simple',
    frequency: 'hourly',
    minutes: [],
    hours: [],
    days: [],
    months: [],
    weekdays: [],
  });

  const [selectAll, setSelectAll] = useState({
    minutes: false,
    hours: false,
    days: false,
    months: false,
    weekdays: false,
  });

  // State to control the logs popup modal
  const [showLogsModal, setShowLogsModal] = useState(false);

  // Clear previous cron job and load new details by ID
  useEffect(() => {
    dispatch(clearCronJob());
    dispatch(fetchCronJobById(id));
  }, [dispatch, id]);

  // When cronJob is loaded, update form fields accordingly
  useEffect(() => {
    if (cronJob) {
      const cronParts = cronJob.cronExpression.split(' ');
      const minutes = cronParts[0] !== '*' ? cronParts[0].split(',') : [];
      const hours = cronParts[1] !== '*' ? cronParts[1].split(',') : [];
      const days = cronParts[2] !== '*' ? cronParts[2].split(',') : [];
      const months = cronParts[3] !== '*' ? cronParts[3].split(',') : [];
      const weekdays = cronParts[4] !== '*' ? cronParts[4].split(',') : [];

      setSelectAll({
        minutes: minutes.length === 60,
        hours: hours.length === 24,
        days: days.length === 31,
        months: months.length === 12,
        weekdays: weekdays.length === 7,
      });

      setFormData({
        name: cronJob.name || '',
        command: cronJob.command || '',
        description: cronJob.description || '',
        status: cronJob.status || 'active',
        scheduleType: cronJob.scheduleType || 'simple',
        frequency: cronJob.frequency || 'hourly',
        minutes,
        hours,
        days,
        months,
        weekdays,
      });
    }
  }, [cronJob]);

  // Once the cron job is loaded, fetch logs for that specific cron job ID
  useEffect(() => {
    if (cronJob && cronJob.id) {
      dispatch(fetchCronLogs(cronJob.id));
    }
  }, [cronJob, dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedCronJob = { ...formData };
    dispatch(updateCronJobs({ ids: [id], cronJob: updatedCronJob }));
    navigate('/');
  };

  const handleClone = () => {
    const clonedCronJob = { ...formData, name: `${formData.name} - Clone` };
    navigate(`/create`, { state: { cronJob: clonedCronJob } });
  };

  const handleRunNow = async () => {
    try {
      const curlCommand = formData.command;
      const commandWithoutK = curlCommand.replace(/-k\s+/gi, '');

      const methodMatch =
        commandWithoutK.match(/--request\s+(\w+)/i) ||
        commandWithoutK.match(/-X\s+(\w+)/i);
      const method = methodMatch ? methodMatch[1].toUpperCase() : 'GET';

      const urlMatch = commandWithoutK.match(/['"]((http|https):\/\/[^'"]+)['"]/i);
      const url = urlMatch ? urlMatch[1] : null;

      if (!url) {
        alert('Invalid command. Please provide a valid curl command.');
        return;
      }

      const headers = {};
      const headerMatches = commandWithoutK.match(/-H\s+['"]([^:'"]+):\s*([^'"]+)['"]/gi);
      if (headerMatches) {
        headerMatches.forEach((header) => {
          const match = header.match(/-H\s+['"]([^:'"]+):\s*([^'"]+)['"]/i);
          if (match) {
            const [, key, value] = match;
            headers[key.trim()] = value.trim();
          }
        });
      }

      console.log('Executing Request:', { method, url, headers });
      const response = await fetch(url, { method, headers });
      if (response.ok) {
        const data = await response.json();
        alert('Cron Job executed successfully: ' + JSON.stringify(data));
      } else {
        const errorData = await response.json();
        alert('Error executing Cron Job: ' + JSON.stringify(errorData));
      }
    } catch (error) {
      alert('Failed to execute Cron Job: ' + error.message);
    }
  };


  const handleScheduleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMultiSelect = (field, options) => {
    setFormData((prev) => ({
      ...prev,
      [field]: Array.from(options, (opt) => opt.value),
    }));
  };

  const handleSelectAllToggle = (field, allValues) => {
    setSelectAll((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
    setFormData((prev) => ({
      ...prev,
      [field]: selectAll[field] ? [] : allValues.map((v) => v.toString()),
    }));
  };

  const createMultiSelectSection = (label, field, options, optionNames) => (
    <div>
      <label>{label}:</label>
      <div className="flex gap-4">
        <div>
          <input
            type="radio"
            name={`${field}Mode`}
            checked={selectAll[field]}
            onChange={() => handleSelectAllToggle(field, options)}
          />
          <label>All</label>
        </div>
        <div>
          <input
            type="radio"
            name={`${field}Mode`}
            checked={!selectAll[field]}
            onChange={() => handleSelectAllToggle(field, [])}
          />
          <label>Selected</label>
        </div>
      </div>
      <select
        multiple
        disabled={selectAll[field]}
        value={formData[field]}
        className="w-full"
        onChange={(e) => handleMultiSelect(field, e.target.selectedOptions)}
      >
        {options.map((option, index) => (
          <option key={option} value={option}>
            {optionNames[index]}
          </option>
        ))}
      </select>
    </div>
  );

  const monthsNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const weekdaysNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="w-full flex justify-center">
      <div className="w-11/12 inline-block mt-24">
        <Navbar
          title="Update Cron Job"
          subtitle="Edit the details of your scheduled task."
          buttonLabel="Back To Dashboard"
          buttonAction={() => navigate('/')}
        />

        <h2 className="text-xl font-semibold mb-6">
          Streamline Your Workflow – Schedule Tasks with Ease!
        </h2>

        {cronJob ? (
          <>
            <form onSubmit={handleSubmit}>
              {/* Execution Details */}
              <div className="mb-6">
                <div className="bg-[#4B76A7] text-white px-4 py-2 mb-4">
                  Execution Details
                </div>
                <div className="grid grid-cols-3 gap-6 mb-4">
                  <div>
                    <label className="block mb-2">Name</label>
                    <input
                      type="text"
                      placeholder="Enter name"
                      value={formData.name}
                      onChange={(e) =>
                        handleScheduleChange('name', e.target.value)
                      }
                      required
                      className="w-full p-2 border rounded-lg shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block mb-2">Command</label>
                    <input
                      type="text"
                      placeholder="Enter command"
                      value={formData.command}
                      onChange={(e) =>
                        handleScheduleChange('command', e.target.value)
                      }
                      required
                      className="w-full p-2 border rounded-lg shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block mb-2">Description</label>
                    <input
                      type="text"
                      placeholder="Enter description"
                      value={formData.description}
                      onChange={(e) =>
                        handleScheduleChange('description', e.target.value)
                      }
                      className="w-full p-2 border rounded-lg shadow-sm"
                    />
                  </div>
                </div>

                <div className="inline-flex rounded-md px-4 py-2 overflow-hidden border">
                  <button
                    type="button"
                    onClick={() => handleScheduleChange('status', 'active')}
                    className={`px-4 py-1 rounded-lg ${formData.status === 'active'
                        ? 'bg-[#8B74BD] text-white'
                        : 'bg-white'
                      }`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => handleScheduleChange('status', 'inactive')}
                    className={`px-4 py-1 rounded-lg ${formData.status === 'inactive'
                        ? 'bg-[#8B74BD] text-white'
                        : 'bg-white'
                      }`}
                  >
                    Inactive
                  </button>
                </div>
              </div>

              {/* Schedule Timing */}
              <div className="mb-6">
                <div className="bg-[#4B76A7] text-white px-4 py-2 flex items-center">
                  <span className="mr-4">Schedule Timing</span>
                  <div className="inline-flex rounded-md px-4 py-2 overflow-hidden bg-white">
                    <button
                      type="button"
                      onClick={() =>
                        handleScheduleChange('scheduleType', 'simple')
                      }
                      className={`px-4 py-1 rounded-lg ${formData.scheduleType === 'simple'
                          ? 'bg-[#8B74BD] text-white'
                          : 'text-gray-700'
                        }`}
                    >
                      Simple
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleScheduleChange('scheduleType', 'custom')
                      }
                      className={`px-4 py-1 rounded-lg ${formData.scheduleType === 'custom'
                          ? 'bg-[#8B74BD] text-white'
                          : 'text-gray-700'
                        }`}
                    >
                      Custom
                    </button>
                  </div>
                </div>

                {formData.scheduleType === 'simple' ? (
                  <div className="mt-4">
                    <label className="block mb-2">Frequency</label>
                    <select
                      value={formData.frequency}
                      onChange={(e) =>
                        handleScheduleChange('frequency', e.target.value)
                      }
                      className="w-48 p-2 border rounded-lg shadow-sm"
                    >
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily (AT Midnight)</option>
                      <option value="weekly">Weekly (On Sunday)</option>
                      <option value="monthly">Monthly (On The 1st)</option>
                    </select>
                  </div>
                ) : (
                  <div className="grid grid-cols-5 gap-4 mt-4">
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {createMultiSelectSection(
                        'Minutes',
                        'minutes',
                        Array.from({ length: 60 }, (_, i) => i.toString()),
                        Array.from({ length: 60 }, (_, i) => i.toString())
                      )}
                    </div>
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {createMultiSelectSection(
                        'Hours',
                        'hours',
                        Array.from({ length: 24 }, (_, i) => i.toString()),
                        Array.from({ length: 24 }, (_, i) => i.toString())
                      )}
                    </div>
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {createMultiSelectSection(
                        'Days',
                        'days',
                        Array.from({ length: 31 }, (_, i) => (i + 1).toString()),
                        Array.from({ length: 31 }, (_, i) => (i + 1).toString())
                      )}
                    </div>
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {createMultiSelectSection(
                        'Months',
                        'months',
                        Array.from({ length: 12 }, (_, i) => (i + 1).toString()),
                        monthsNames
                      )}
                    </div>
                    <div className="border rounded-lg p-2 max-h-[300px] overflow-y-auto">
                      {createMultiSelectSection(
                        'Weekdays',
                        'weekdays',
                        Array.from({ length: 7 }, (_, i) => i.toString()),
                        weekdaysNames
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mb-4">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-[#4B76A7] to-[#24486C] hover:opacity-90 text-white px-6 py-2 rounded-lg shadow-md hover:bg-[#3d6087]"
                >
                  Save Cron Job
                </button>
                <button
                  type="button"
                  onClick={handleClone}
                  className="ml-2 px-6 py-2 bg-purple-600 hover:opacity-90 text-white border rounded-lg shadow-md"
                >
                  Clone
                </button>
                <button
                  type="button"
                  onClick={handleRunNow}
                  className="bg-green-700 hover:opacity-90 text-white ml-2 px-6 py-2 border rounded-lg shadow-md"
                >
                  Run Now
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogsModal(true)}
                  className="bg-blue-600 hover:opacity-90 text-white ml-2 px-6 py-2 border rounded-lg shadow-md"
                >
                  View Logs
                </button>
              </div>
            </form>

            {/* Render the logs in a modal popup */}
            {showLogsModal && (
              <Modal onClose={() => setShowLogsModal(false)}>
                <h3 className="text-xl font-bold mb-4">Cron Job Logs</h3>
                <CronLogsComponent logs={cronLogs} loading={loading} error={error} />
              </Modal>
            )}
          </>
        ) : (
          <p>Loading cron job data...</p>
        )}
      </div>
    </div>
  );
};

export default UpdateCronJob;
